/**
 * Shared helper: read the current project's app name from `./wrangler.toml`.
 *
 * Used by commands that operate on "the current app" — `deploy`, `domain buy`,
 * `domain attach`, etc. Lets agents and scripts run from inside a project
 * directory without redundantly passing `--app <name>`.
 */

import { existsSync, readFileSync, readdirSync, writeFileSync, mkdirSync } from 'node:fs'
import { join, resolve, dirname } from 'node:path'
import { parse as parseToml } from 'smol-toml'

/**
 * Read the app name from `wrangler.toml` in the given directory (default cwd).
 * Returns null if no wrangler.toml exists or the file lacks a `name` field.
 */
export function detectAppName(cwd: string = process.cwd()): string | null {
  const wranglerPath = join(resolve(cwd), 'wrangler.toml')
  if (!existsSync(wranglerPath)) return null
  try {
    const cfg = parseToml(readFileSync(wranglerPath, 'utf-8')) as { name?: string }
    return typeof cfg.name === 'string' && cfg.name.length > 0 ? cfg.name : null
  } catch {
    return null
  }
}

/**
 * Resolve the app name from explicit arg or wrangler.toml.
 * Throws with a clear message if neither is available.
 */
export function requireAppName(explicit: string | undefined, cwd?: string): string {
  if (explicit) return explicit
  const detected = detectAppName(cwd)
  if (detected) return detected
  throw new Error(
    'No app specified. Pass --app <name>, or run from an app directory with a wrangler.toml.',
  )
}

/**
 * Walk upward from `start` (default cwd) to the nearest directory containing a
 * `wrangler.toml`, returning that directory — or null if none is found before
 * the filesystem root.
 *
 * This makes the dev-loop commands robust to being run from a *subdirectory*
 * of an app instead of hard-failing the moment cwd isn't exactly the app root.
 * (It does NOT recover the inverse case — cwd sitting at the app's *parent*;
 * for that, `findChildApps` powers a "did you mean cd <app>" hint.)
 */
export function findAppDir(start: string = process.cwd()): string | null {
  let dir = resolve(start)
  for (;;) {
    if (existsSync(join(dir, 'wrangler.toml'))) return dir
    const parent = dirname(dir)
    if (parent === dir) return null // reached filesystem root
    dir = parent
  }
}

/**
 * Immediate subdirectories of `dir` that look like an app (contain a
 * `wrangler.toml`). Used to turn a bare "no wrangler.toml here" failure into an
 * actionable hint when the caller is sitting one level above the app — the
 * exact situation a harness creates when it resets cwd to the parent.
 */
export function findChildApps(dir: string): string[] {
  try {
    return readdirSync(dir, { withFileTypes: true })
      .filter((e) => e.isDirectory() && existsSync(join(dir, e.name, 'wrangler.toml')))
      .map((e) => e.name)
      .sort()
  } catch {
    return []
  }
}

/**
 * Seed `<appDir>/.claude/launch.json` so the Claude Code preview tool launches
 * THIS app. Without an app-local launch.json the tool walks up the directory
 * tree and can latch onto an ancestor repo's config — pointing at a different
 * app and port — which is a real failure when a DeepSpace app lives inside
 * another repo.
 *
 * Writes only when the file is absent, so a user- or tool-customized config
 * (different name, port, or runner) is never clobbered. Best-effort: a write
 * failure must never block `dev` from starting.
 */
export function writeLaunchConfigIfMissing(appDir: string, appName: string, port: number): void {
  const launchPath = join(appDir, '.claude', 'launch.json')
  if (existsSync(launchPath)) return
  const config = {
    version: '0.0.1',
    configurations: [
      {
        name: appName,
        runtimeExecutable: 'npx',
        runtimeArgs: ['deepspace', 'dev', '--port', String(port)],
        port,
      },
    ],
  }
  try {
    mkdirSync(join(appDir, '.claude'), { recursive: true })
    writeFileSync(launchPath, JSON.stringify(config, null, 2) + '\n')
  } catch {
    // best-effort only
  }
}
